import Icon from '@components/Icon';
import { extraQueryParams } from '@utils/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

interface UnloggedSignupModalProps {
  isOpen: boolean;
  onClose?: () => void;
}

export const UnloggedSignupModal: React.FC<UnloggedSignupModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { signinRedirect } = useAuth();

  const handleSignUp = () => {
    signinRedirect({
      extraQueryParams: { ...extraQueryParams, screen_hint: 'signup' },
    }).catch((error) => {
      console.error('Error during sign-up redirect:', error);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999999]">
      {/* Modal Container - Responsive for mobile and desktop */}
      <div className="bg-[rgba(64,64,64,0.8)] backdrop-blur-[12px] md:backdrop-blur-[18px] rounded-[16px] md:rounded-[24px] p-5 md:p-7 w-[295px] md:w-[442.5px] flex flex-col items-center relative">
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-white/80 transition-colors duration-200"
            aria-label={t('signupModal.closeModal')}>
            <Icon name="cross" size={20} color="#FFFFFF" />
          </button>
        )}

        {/* Napster logo Icon */}
        <div className="w-16 h-16 flex items-center justify-center">
          <svg
            width="50"
            height="48"
            viewBox="0 0 50 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M42.0906 8.0263C41.6392 8.02588 41.188 8.05437 40.74 8.11152H40.6499L40.592 8.03941C38.7178 5.53961 36.3034 3.51388 33.5365 2.1196C30.7695 0.725313 27.7243 0 24.6377 0C21.5511 0 18.5061 0.725313 15.7391 2.1196C12.9721 3.51388 10.5577 5.53961 8.68337 8.03941L8.63192 8.11152H8.54187C8.09167 8.05455 7.63841 8.02609 7.18475 8.0263H4.47049V13.1599L4.38044 13.2058C3.07078 13.833 1.96334 14.8274 1.18764 16.0726C0.411933 17.3178 0 18.7623 0 20.2374C0 21.7124 0.411933 23.1569 1.18764 24.4021C1.96334 25.6473 3.07078 26.6416 4.38044 27.2689L4.47049 27.3083V27.4132C4.47049 32.8732 6.59829 38.1096 10.3858 41.9702C14.1733 45.8309 19.3103 48 24.6667 48C30.023 48 35.1599 45.8309 38.9475 41.9702C42.7352 38.1096 44.863 32.8732 44.863 27.4132V27.3083L44.9528 27.2689C46.2627 26.6416 47.3701 25.6473 48.1457 24.4021C48.9214 23.1569 49.3333 21.7124 49.3333 20.2374C49.3333 18.7623 48.9214 17.3178 48.1457 16.0726C47.3701 14.8274 46.2627 13.833 44.9528 13.2058L44.863 13.1599V8.0263H42.0906ZM13.9704 9.7047C15.3668 8.26754 17.0288 7.12671 18.8603 6.34816C20.6918 5.56959 22.6566 5.16873 24.6409 5.16873C26.6252 5.16873 28.59 5.56959 30.4217 6.34816C32.2531 7.12671 33.9149 8.26754 35.3115 9.7047L35.4595 9.86205L35.2728 9.96696C34.505 10.3965 33.7677 10.8807 33.0669 11.4159L32.983 11.4749L32.8994 11.429C30.3415 10.0925 27.5151 9.37673 24.6409 9.33755C21.7668 9.37673 18.9404 10.0925 16.3824 11.429L16.2923 11.4749L16.2151 11.4159C15.5118 10.8806 14.7725 10.3965 14.0026 9.96696L13.816 9.84237L13.9704 9.7047ZM39.3442 27.2429C39.3442 31.2176 37.7952 35.0299 35.0379 37.8407C32.2803 40.6513 28.5405 42.2304 24.6409 42.2304C20.7414 42.2304 17.0015 40.6513 14.2441 37.8407C11.4867 35.0299 9.93761 31.2176 9.93761 27.2429V14.2154L10.137 14.2679C12.7098 14.9235 15.1475 17.8017 15.7135 18.5032C16.8953 17.3356 18.2933 16.4195 19.8261 15.8083C21.3588 15.1971 22.9956 14.903 24.6409 14.9432C26.2862 14.903 27.9232 15.1971 29.4558 15.8083C30.9886 16.4195 32.3865 17.3356 33.5684 18.5032C34.1281 17.8017 36.5464 14.9235 39.1448 14.2679L39.3442 14.2154V27.2429Z"
              fill="white"
            />
          </svg>
        </div>

        {/* Text Content - Responsive typography */}
        <div className="text-center mb-6 md:mb-7">
          <h2 className="font-dm-sans text-[20px] md:text-[28px] font-semibold leading-[24px] md:leading-[32px] tracking-[-0.4px] md:tracking-[-0.28px] text-white">
            {t('signupModal.createYour')}
          </h2>
          <h2 className="font-dm-sans text-[20px] md:text-[28px] font-semibold leading-[24px] md:leading-[32px] tracking-[-0.4px] md:tracking-[-0.28px] text-white">
            {t('signupModal.ownSongOnNapster')}
          </h2>
        </div>

        {/* Sign Up Button - Responsive sizing */}
        <button
          onClick={handleSignUp}
          className="bg-white rounded-[999px] md:rounded-[1332px] h-12 md:h-16 px-4 md:px-[21.333px] font-season-sans font-[650] text-[14px] md:text-[18px] leading-[17.198px] md:leading-[22px] hover:bg-white/90 transition-colors duration-200 w-full md:w-[386.5px] text-[#0F0602] btn-signup-modal flex items-center justify-center">
          {t('signupModal.signUpForFree')}
        </button>
      </div>
    </div>
  );
};

export default UnloggedSignupModal;
